import { useState, useEffect, useCallback, useRef } from 'react';
import ChartHeader from '../common/ChartHeader';
import SearchResultCardSkeleton from '../search/skeletons/SearchResultCardSkeleton';
import { Link } from 'react-router-dom';
import { useAuth } from '../../firebase/useAuth';
import { useModal } from '../../context/ModalContext';
import SearchResultCard from '../search/SearchResultCard';
import DetailsModal from '../modals/DetailsModal/DetailsModal';
import {
  fetchCramWordRefs,
  fetchCramSentenceRefs,
  fetchRecentUnlocksWordRefs,
  fetchRecentUnlocksSentenceRefs,
  resolveWordRef,
  resolveSentenceRef,
} from '../../firebase/extraStudyPreview';


const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ActionButton = ({ to, accentColor, accentColorTo, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`shrink-0 inline-flex items-center justify-center gap-0.5 self-stretch w-20 sm:w-28 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 active:scale-95${hovered ? '' : ' gradient-border-btn'}`}
      style={hovered ? {
        background: `linear-gradient(135deg, ${accentColor}, ${accentColorTo ?? accentColor})`,
        color: '#fff',
      } : {
        '--btn-color-from': accentColor,
        '--btn-color-to': accentColorTo ?? accentColor,
        color: accentColor,
      }}
    >
      {children}
    </Link>
  );
};

// Carousel for one subsection (cram or recent unlocks)
const CarouselSection = ({ refs, resolveFn, totalCount, itemType, accentColor, accentColorTo, label, actionPath, actionVerb, onItemClick, learnedIds }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [resolvedCache, setResolvedCache] = useState({});
  const [resolving, setResolving] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset carousel when refs change (e.g. tab switch on mobile)
  useEffect(() => {
    setCurrentIdx(0);
    setResolvedCache({});
    setResolving(false);
  }, [refs]);

  useEffect(() => {
    if (!refs || refs.length === 0) return;
    if (resolvedCache[currentIdx] !== undefined) return;

    const ref = refs[currentIdx];
    if (!ref) return;

    setResolving(true);
    resolveFn(ref).then((resolved) => {
      if (!mountedRef.current) return;
      setResolvedCache((prev) => ({ ...prev, [currentIdx]: resolved || null }));
      setResolving(false);
    }).catch(() => {
      if (!mountedRef.current) return;
      setResolvedCache((prev) => ({ ...prev, [currentIdx]: null }));
      setResolving(false);
    });
  }, [currentIdx, refs, resolveFn, resolvedCache]);

  if (!refs || refs.length === 0) return null;

  const currentItem = resolvedCache[currentIdx];
  const isLoading = resolving || resolvedCache[currentIdx] === undefined;
  const displayCount = totalCount ?? refs.length;

  return (
    <div>
      {/* Label row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-1 h-3.5 rounded-full" style={{ background: accentColor }} />
          <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">{label}</span>
          <span className="text-xs font-bold" style={{ color: accentColor }}>{displayCount}</span>
        </div>
        {/* Counter + arrows */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
            disabled={currentIdx === 0}
            className="p-1 rounded-md transition-colors disabled:opacity-30 hover:bg-background-tertiary disabled:cursor-not-allowed text-content-secondary"
            aria-label="Previous item"
          >
            <ChevronLeft />
          </button>
          <span className="text-xs font-medium text-content-secondary tabular-nums min-w-[2.5rem] text-center">
            {currentIdx + 1} / {refs.length}
            {totalCount > refs.length ? '+' : ''}
          </span>
          <button
            onClick={() => setCurrentIdx((i) => Math.min(refs.length - 1, i + 1))}
            disabled={currentIdx >= refs.length - 1}
            className="p-1 rounded-md transition-colors disabled:opacity-30 hover:bg-background-tertiary disabled:cursor-not-allowed text-content-secondary"
            aria-label="Next item"
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      {/* Item: no badges on mobile, badges + inline button on sm+ */}
      <div>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <SearchResultCardSkeleton compact />
            ) : currentItem ? (
              <SearchResultCard
                item={currentItem}
                type={itemType}
                onClick={() => onItemClick(currentItem)}
                variant="default"
                compact
                learnedIds={learnedIds}
              />
            ) : (
              <div className="h-10 flex items-center justify-center text-xs text-content-tertiary">
                Failed to load item
              </div>
            )}
          </div>
          <ActionButton to={actionPath} accentColor={accentColor} accentColorTo={accentColorTo}>
            <span>{actionVerb}</span>
            <span className="tabular-nums">{displayCount}</span>
            <ChevronRight />
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

const EmptySubsection = ({ accentColor, label, message }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className="w-1 h-3.5 rounded-full opacity-30" style={{ background: accentColor }} />
        <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">{label}</span>
        <span className="text-xs font-bold opacity-40" style={{ color: accentColor }}>0</span>
      </div>
      <div className="flex items-center gap-1 invisible" aria-hidden="true">
        <button className="p-1 rounded-md"><ChevronLeft /></button>
        <span className="text-xs tabular-nums min-w-[2.5rem] text-center">0 / 0</span>
        <button className="p-1 rounded-md"><ChevronRight /></button>
      </div>
    </div>
    <div className="flex items-center px-3 py-2.5 rounded-lg border border-dashed border-border">
      <span className="text-sm text-content-secondary">{message}</span>
    </div>
  </div>
);

// Inner content only — no card shell
const CardContent = ({
  accentColorUnlock,
  accentColorUnlockTo,
  accentColorCram,
  accentColorCramTo,
  unlockRefs,
  unlockCount,
  cramRefs,
  cramCount,
  unlockPath,
  cramPath,
  itemType,
  resolveFn,
  refsLoading,
  onItemClick,
  learnedIds,
}) => {
  const hasUnlocks = unlockCount > 0;
  const hasCram = cramCount > 0;

  const unlockSection = hasUnlocks
    ? refsLoading
      ? (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-3.5 rounded-full bg-background-tertiary animate-pulse" />
            <div className="h-3 w-28 bg-background-tertiary rounded animate-pulse" />
          </div>
          <SearchResultCardSkeleton compact />
        </div>
      )
      : (
        <CarouselSection
          refs={unlockRefs}
          resolveFn={resolveFn}
          totalCount={unlockCount}
          itemType={itemType}
          accentColor={accentColorUnlock}
          accentColorTo={accentColorUnlockTo}
          label="Recently Unlocked"
          actionPath={unlockPath}
          actionVerb="Drill"
          onItemClick={onItemClick}
          learnedIds={learnedIds}
        />
      )
    : (
      <EmptySubsection
        accentColor={accentColorUnlock}
        label="Recently Unlocked"
        message="No recently unlocked items"
      />
    );

  const cramSection = hasCram
    ? refsLoading
      ? (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-3.5 rounded-full bg-background-tertiary animate-pulse" />
            <div className="h-3 w-28 bg-background-tertiary rounded animate-pulse" />
          </div>
          <SearchResultCardSkeleton compact />
        </div>
      )
      : (
        <CarouselSection
          refs={cramRefs}
          resolveFn={resolveFn}
          totalCount={cramCount}
          itemType={itemType}
          accentColor={accentColorCram}
          accentColorTo={accentColorCramTo}
          label="Recent Mistakes"
          actionPath={cramPath}
          actionVerb="Cram"
          onItemClick={onItemClick}
          learnedIds={learnedIds}
        />
      )
    : (
      <EmptySubsection
        accentColor={accentColorCram}
        label="Recent Mistakes"
        message="No recent mistakes — nice work!"
      />
    );

  return (
    <div className="px-5 pb-3 flex flex-col gap-3 flex-1">
      <div className="flex-1 flex flex-col justify-start">
        {unlockSection}
      </div>
      <div className="border-t border-border shrink-0" />
      <div className="flex-1 flex flex-col justify-start">
        {cramSection}
      </div>
    </div>
  );
};

const ExtraStudySection = ({
  cramSentencesCount,
  cramWordsCount,
  recentUnlocksSentencesCount,
  recentUnlocksWordsCount,
  learnedWordIds,
  learnedSentenceIds,
}) => {
  const { userId } = useAuth();
  const { showModal, hideModal } = useModal();

  const [refs, setRefs] = useState({
    cramWordRefs: [],
    cramSentenceRefs: [],
    unlockWordRefs: [],
    unlockSentenceRefs: [],
    loading: true,
  });

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [cramWordRefs, cramSentenceRefs, unlockWordRefs, unlockSentenceRefs] = await Promise.all([
          cramWordsCount > 0 ? fetchCramWordRefs(userId) : Promise.resolve([]),
          cramSentencesCount > 0 ? fetchCramSentenceRefs(userId) : Promise.resolve([]),
          recentUnlocksWordsCount > 0 ? fetchRecentUnlocksWordRefs(userId) : Promise.resolve([]),
          recentUnlocksSentencesCount > 0 ? fetchRecentUnlocksSentenceRefs(userId) : Promise.resolve([]),
        ]);
        if (!cancelled) setRefs({ cramWordRefs, cramSentenceRefs, unlockWordRefs, unlockSentenceRefs, loading: false });
      } catch (err) {
        console.error('Error fetching extra study refs:', err);
        if (!cancelled) setRefs((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [userId, cramWordsCount, cramSentencesCount, recentUnlocksWordsCount, recentUnlocksSentencesCount]);

  const handleItemClick = useCallback(
    (item) => showModal(<DetailsModal item={item} handleClose={hideModal} />),
    [showModal, hideModal]
  );

  const hasAnything =
    recentUnlocksSentencesCount > 0 ||
    recentUnlocksWordsCount > 0 ||
    cramSentencesCount > 0 ||
    cramWordsCount > 0;

  const [activeTab, setActiveTab] = useState('sentences');

  if (!hasAnything) return null;

  const sharedSentenceProps = {
    accentColorUnlock: 'var(--color-session-learn)',
    accentColorUnlockTo: 'var(--color-session-learn-accent)',
    accentColorCram: 'var(--color-session-review)',
    accentColorCramTo: 'var(--color-session-review-accent)',
    unlockRefs: refs.unlockSentenceRefs,
    unlockCount: recentUnlocksSentencesCount,
    cramRefs: refs.cramSentenceRefs,
    cramCount: cramSentencesCount,
    unlockPath: '/session/recent-unlocks/sentences',
    cramPath: '/session/cram/sentences',
    itemType: 'sentences',
    resolveFn: resolveSentenceRef,
    refsLoading: refs.loading,
    onItemClick: handleItemClick,
    learnedIds: learnedSentenceIds,
  };

  const sharedWordProps = {
    accentColorUnlock: 'var(--color-session-learn)',
    accentColorUnlockTo: 'var(--color-session-learn-accent)',
    accentColorCram: 'var(--color-session-review)',
    accentColorCramTo: 'var(--color-session-review-accent)',
    unlockRefs: refs.unlockWordRefs,
    unlockCount: recentUnlocksWordsCount,
    cramRefs: refs.cramWordRefs,
    cramCount: cramWordsCount,
    unlockPath: '/session/recent-unlocks/words',
    cramPath: '/session/cram/words',
    itemType: 'words',
    resolveFn: resolveWordRef,
    refsLoading: refs.loading,
    onItemClick: handleItemClick,
    learnedIds: learnedWordIds,
  };

  return (
    <div>
      {/* Mobile + tablet: tabbed single card */}
      <div className="lg:hidden relative overflow-hidden rounded-2xl border-2 border-border bg-background-secondary/70 backdrop-blur-sm">
        <div className="flex pb-3">
          {[
            { key: 'sentences', label: 'Sentences Extra' },
            { key: 'words', label: 'Words Extra' },
          ].map(({ key, label }, i) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`
                flex-1 py-3 text-sm font-medium transition-all duration-200
                ${i === 0 ? 'rounded-tl-2xl' : 'rounded-tr-2xl'}
                ${activeTab === key
                  ? 'border-b-2 border-primary bg-background-secondary/70 backdrop-blur-sm text-primary'
                  : 'border-b-2 border-border bg-background-secondary/70 backdrop-blur-sm text-content-secondary hover:text-content-primary'
                }
              `}
            >
              {label}
            </button>
          ))}
        </div>
        <CardContent {...(activeTab === 'sentences' ? sharedSentenceProps : sharedWordProps)} />
      </div>

      {/* Large desktop: two separate cards matching forecast pattern */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-6 items-stretch">
        <div className="relative overflow-hidden rounded-2xl border-2 border-border bg-background-secondary/70 backdrop-blur-sm flex flex-col">
          <ChartHeader title="Sentences Extra" className="pt-4 sm:pt-6" />
          <CardContent {...sharedSentenceProps} />
        </div>
        <div className="relative overflow-hidden rounded-2xl border-2 border-border bg-background-secondary/70 backdrop-blur-sm flex flex-col">
          <ChartHeader title="Words Extra" className="pt-4 sm:pt-6" />
          <CardContent {...sharedWordProps} />
        </div>
      </div>
    </div>
  );
};

export default ExtraStudySection;
